import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Alert,
  CircularProgress,
  Chip,
  InputAdornment,
  Checkbox,
  Tooltip,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  LocalShipping as LocalShippingIcon,
  Inventory as InventoryIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
  ContentCopy as ContentCopyIcon,
  Visibility as VisibilityIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import api from '../services/api';
import { useSnackbar } from 'notistack';

interface FukubukuroProduct {
  id: number;
  name: string;
  description: string;
  price: number;
  discount_price: number;
  image_url: string;
  all_images?: string[];
  available_quantity: number;
  total_quantity: number;
  is_active: boolean;
  category?: string;
  tags?: string[];
  created_at: string;
  updated_at: string;
}

interface FukubukuroOrder {
  id?: number;
  order_id: string;
  fukubukuro_product_id?: number;
  customer_id?: number;
  customer_phone: string;
  customer_name: string;
  customer_email?: string;
  height: number;
  weight: number;
  waist?: number;
  notes?: string;
  quantity?: number;
  price: string | number;
  discount_price: string | number;
  payment_method: string;
  payment_no?: string;
  cvs_validation_no?: string;
  delivery_method: string;
  store_id?: string;
  store_name?: string;
  store_address?: string;
  logistics_sub_type?: string;
  tracking_number?: string;
  ecpay_logistics_id?: string;
  status: string;
  is_received?: boolean;
  is_sent?: boolean;
  created_at?: string;
  updated_at?: string;
  product_name?: string;
  product_image?: string;
}

function FukubukuroFullAdminPage() {
  const { enqueueSnackbar } = useSnackbar();
  
  // Tab state
  const [tabValue, setTabValue] = useState(0); // 0: Products, 1: Orders
  const [productTabValue, setProductTabValue] = useState(0); // 0: All, 1: Active, 2: Inactive
  
  // Product states
  const [products, setProducts] = useState<FukubukuroProduct[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<FukubukuroProduct | null>(null);
  
  // Product form data
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: 0,
    discount_price: 0,
    image_url: '',
    all_images: [''],
    available_quantity: 0,
    is_active: true,
    category: '',
    tags: ''
  });
  
  // Order states
  const [orders, setOrders] = useState<FukubukuroOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Shipping dialog state
  const [shippingDialogOpen, setShippingDialogOpen] = useState(false);
  const [currentOrder, setCurrentOrder] = useState<FukubukuroOrder | null>(null);
  const [processingShipping, setProcessingShipping] = useState(false);
  const [editableAmount, setEditableAmount] = useState<string>('');
  const [editableName, setEditableName] = useState<string>('');
  const [editablePhone, setEditablePhone] = useState<string>('');

  
  // Batch printing state
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [batchPrintDialogOpen, setBatchPrintDialogOpen] = useState(false);
  const [batchPrintLoading, setBatchPrintLoading] = useState(false);
  
  // Image gallery state
  const [imageGalleryOpen, setImageGalleryOpen] = useState(false);
  const [currentProductImages, setCurrentProductImages] = useState<string[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedProductTitle, setSelectedProductTitle] = useState('');
  const [showUnshippedOnly, setShowUnshippedOnly] = useState(true);
  // Orders marked as sent in this session — stay visible until next fetchOrders()
  const [recentlyShipped, setRecentlyShipped] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchProducts();
    fetchOrders();
  }, []);

  // ============ PRODUCT MANAGEMENT FUNCTIONS ============
  
  const fetchProducts = async () => {
    try {
      setLoadingProducts(true);
      const response = await api.get('/api/fukubukuro/admin/products', {
        params: { limit: 100, offset: 0 }
      });
      if (response.data.success) {
        setProducts(response.data.products);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      enqueueSnackbar('載入商品失敗', { variant: 'error' });
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleOpenDialog = (product?: FukubukuroProduct) => {
  if (product) {
    setEditingProduct(product);
    
    // ✅ FIX: Ensure all_images is always an array
    let allImages = [''];
    if (product.all_images) {
      if (Array.isArray(product.all_images)) {
        // Already an array - use it if not empty
        allImages = product.all_images.length > 0 ? product.all_images : [''];
      } else if (typeof product.all_images === 'string') {
        // If it's a string, try to parse it as JSON
        try {
          const parsed = JSON.parse(product.all_images);
          allImages = Array.isArray(parsed) && parsed.length > 0 ? parsed : [''];
        } catch {
          // If parsing fails, use default
          allImages = [''];
        }
      }
    }
    
    setFormData({
      name: product.name,
      description: product.description,
      price: product.price,
      discount_price: product.discount_price,
      image_url: product.image_url,
      all_images: allImages,  // ✅ Now guaranteed to be an array
      available_quantity: product.available_quantity,
      is_active: product.is_active,
      category: product.category || '',
      tags: Array.isArray(product.tags) ? product.tags.join(', ') : ''  // ✅ Also protect tags
    });
  } else {
    setEditingProduct(null);
    setFormData({
      name: '',
      description: '',
      price: 0,
      discount_price: 0,
      image_url: '',
      all_images: [''],
      available_quantity: 0,
      is_active: true,
      category: '',
      tags: ''
    });
  }
  setDialogOpen(true);
};

  const handleSaveProduct = async () => {
    try {
      const productData = {
        ...formData,
        all_images: formData.all_images.filter(img => img.trim() !== ''),
        tags: formData.tags.split(',').map(t => t.trim()).filter(t => t !== '')
      };

      if (editingProduct) {
        await api.put(`/api/fukubukuro/admin/products/${editingProduct.id}`, productData);
        enqueueSnackbar('商品已更新', { variant: 'success' });
      } else {
        await api.post('/api/fukubukuro/admin/products', {
          ...productData,
          total_quantity: productData.available_quantity
        });
        enqueueSnackbar('商品已新增', { variant: 'success' });
      }

      setDialogOpen(false);
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      enqueueSnackbar('儲存失敗', { variant: 'error' });
    }
  };

  const handleToggleActive = async (product: FukubukuroProduct) => {
    try {
      await api.put(`/api/fukubukuro/admin/products/${product.id}`, {
        is_active: !product.is_active
      });
      enqueueSnackbar(`商品已${!product.is_active ? '上架' : '停用'}`, { variant: 'success' });
      fetchProducts();
    } catch (error) {
      console.error('Error toggling active status:', error);
      enqueueSnackbar('操作失敗', { variant: 'error' });
    }
  };

  const filteredProducts = products.filter(product => {
    if (productTabValue === 1) return product.is_active;
    if (productTabValue === 2) return !product.is_active;
    return true;
  });

  // ============ ORDER MANAGEMENT FUNCTIONS ============

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/fukubukuro/admin/orders');
      if (response.data.success) {
        setOrders(response.data.orders || []);
        // Reset session-shipped overlay on every fetch
        setRecentlyShipped(new Set());
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      enqueueSnackbar('載入訂單失敗', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Filter orders based on search
  const filteredOrders = orders.filter(order => {
  // First apply search filter
  const matchesSearch = !searchTerm || (
    order.order_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.customer_phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.customer_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.product_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // Then apply shipping status filter:
  // When "unshipped only" is on, keep the order visible if
  //   (a) it's not yet sent, OR
  //   (b) it was just marked sent in this session — until the next fetchOrders()
  const matchesShippingFilter =
    !showUnshippedOnly || !order.is_sent || recentlyShipped.has(order.order_id);

  return matchesSearch && matchesShippingFilter;
});

  // Copy to clipboard
  const copyToClipboard = (text: string | undefined) => {
    if (!text) return;
    navigator.clipboard.writeText(text)
      .then(() => enqueueSnackbar('已複製到剪貼簿', { variant: 'success' }))
      .catch(() => enqueueSnackbar('複製失敗', { variant: 'error' }));
  };

  // Image gallery handlers
  const handleOpenImageGallery = (order: FukubukuroOrder) => {
    const images: string[] = [];
    if (order.product_image) {
      images.push(order.product_image);
    }
    
    if (images.length > 0) {
      setCurrentProductImages(images);
      setCurrentImageIndex(0);
      setSelectedProductTitle(order.product_name || '福袋商品');
      setImageGalleryOpen(true);
    } else {
      enqueueSnackbar('沒有可用的產品圖片', { variant: 'info' });
    }
  };

  const handlePreviousImage = () => {
    setCurrentImageIndex(prev => prev > 0 ? prev - 1 : currentProductImages.length - 1);
  };

  const handleNextImage = () => {
    setCurrentImageIndex(prev => prev < currentProductImages.length - 1 ? prev + 1 : 0);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowLeft') handlePreviousImage();
    else if (event.key === 'ArrowRight') handleNextImage();
    else if (event.key === 'Escape') setImageGalleryOpen(false);
  };

  // Toggle received status
  const toggleReceivedStatus = async (orderId: string, isCurrentlyReceived?: boolean) => {
    const newStatus = !isCurrentlyReceived;
    
    // Optimistic update
    setOrders(prev => prev.map(o => 
      o.order_id === orderId ? { ...o, is_received: newStatus } : o
    ));
    
    try {
      await api.put(`/api/fukubukuro/admin/orders/${orderId}/status`, {
        is_received: newStatus
      });
      enqueueSnackbar(newStatus ? '已標記為已收到' : '已標記為未收到', { variant: 'success' });
    } catch (error) {
      console.error('Error updating received status:', error);
      enqueueSnackbar('更新收款狀態失敗', { variant: 'error' });
      // Revert on error
      setOrders(prev => prev.map(o => 
        o.order_id === orderId ? { ...o, is_received: isCurrentlyReceived } : o
      ));
    }
  };

  // Toggle sent status
  const toggleSentStatus = async (orderId: string, isCurrentlySent?: boolean) => {
    const newStatus = !isCurrentlySent;
    
    // Optimistic update
    setOrders(prev => prev.map(o => 
      o.order_id === orderId ? { ...o, is_sent: newStatus } : o
    ));
    
    try {
      await api.put(`/api/fukubukuro/admin/orders/${orderId}/status`, {
        is_sent: newStatus
      });
      // Keep the row visible in the unshipped-only view until the next fetch
      if (newStatus) {
        setRecentlyShipped(prev => {
          const next = new Set(prev);
          next.add(orderId);
          return next;
        });
      } else {
        setRecentlyShipped(prev => {
          if (!prev.has(orderId)) return prev;
          const next = new Set(prev);
          next.delete(orderId);
          return next;
        });
      }
      enqueueSnackbar(newStatus ? '已標記為已發貨' : '已標記為未發貨', { variant: 'success' });
    } catch (error) {
      console.error('Error updating sent status:', error);
      enqueueSnackbar('更新發貨狀態失敗', { variant: 'error' });
      // Revert on error
      setOrders(prev => prev.map(o => 
        o.order_id === orderId ? { ...o, is_sent: isCurrentlySent } : o
      ));
    }
  };

  // Handle ship order (create logistics if needed)
  const handleShipOrder = async (order: FukubukuroOrder) => {
    if (!order) return;

    const totalAmount = Math.floor(parseFloat(order.discount_price?.toString() || order.price?.toString() || '0'));
    setEditableAmount(totalAmount.toString());
    setEditableName(order.customer_name || '');
    setEditablePhone(order.customer_phone || '');

    const hasTrackingInfo = order.tracking_number || order.payment_no || order.cvs_validation_no;

    if (hasTrackingInfo) {
      setCurrentOrder(order);
      setShippingDialogOpen(true);
      return;
    }

    if (order.ecpay_logistics_id && order.delivery_method === 'pickup') {
      try {
        enqueueSnackbar('正在獲取追蹤號碼...', { variant: 'info' });

        const response = await api.get(`/api/ecpay/logistics/tracking`, {
          params: {
            logisticsId: order.ecpay_logistics_id,
            orderId: order.order_id
          }
        });

        if (response.data?.success) {
          const trackingUpdate = {
            tracking_number: response.data.trackingNumber,
            payment_no: response.data.paymentNo,
            cvs_validation_no: response.data.validationNo,
            ecpay_logistics_id: response.data.logisticsId
          };

          enqueueSnackbar(`已獲取追蹤號碼: ${response.data.trackingNumber}`, { 
            variant: 'success',
            autoHideDuration: 5000
          });

          // Update UI
          setOrders(prev => prev.map(o => 
            o.order_id === order.order_id ? { ...o, ...trackingUpdate } : o
          ));

          setCurrentOrder({ ...order, ...trackingUpdate });
          setShippingDialogOpen(true);
        } else {
          setCurrentOrder(order);
          setShippingDialogOpen(true);
          enqueueSnackbar(response.data?.message || '無法獲取追蹤號碼', { variant: 'warning' });
        }
      } catch (error) {
        console.error('Error getting tracking number:', error);
        setCurrentOrder(order);
        setShippingDialogOpen(true);
        enqueueSnackbar('獲取追蹤號碼時發生錯誤', { variant: 'error' });
      }
    } else {
      setCurrentOrder(order);
      setShippingDialogOpen(true);
    }
  };

  // Confirm ship order
  const confirmShipOrder = async () => {
    if (!currentOrder) return;

    setProcessingShipping(true);

    try {
      // Step 1: Create logistics if needed
      if (currentOrder.delivery_method === 'pickup' && currentOrder.store_id && !currentOrder.ecpay_logistics_id) {
        const finalGoodsAmount = editableAmount ? parseInt(editableAmount) : 100;
        
        const paymentMethodForBackend = currentOrder.payment_method === 'credit_card' 
        ? 'credit_card'   // Already paid → IsCollection: 'N'
        : 'store_cod';    // Pay at store → IsCollection: 'Y'

        const logisticsResponse = await api.post('/api/ecpay/logistics/create-for-fukubukuro-order', {
          orderId: currentOrder.order_id,
          storeId: currentOrder.store_id,
          customerName: editableName || currentOrder.customer_name || currentOrder.customer_phone,
          customerPhone: editablePhone || currentOrder.customer_phone,
          logisticsSubType: currentOrder.logistics_sub_type,
          goodsAmount: finalGoodsAmount,
          storeName: currentOrder.store_name || '',
          paymentMethod: paymentMethodForBackend
        });

        if (logisticsResponse.data?.success) {
          currentOrder.ecpay_logistics_id = logisticsResponse.data.logisticsId;
          currentOrder.payment_no = logisticsResponse.data.paymentNo;
          currentOrder.cvs_validation_no = logisticsResponse.data.validationNo;
          
          // Update order with logistics info
          await api.put(`/api/fukubukuro/admin/orders/${currentOrder.order_id}/status`, {
            ecpay_logistics_id: logisticsResponse.data.logisticsId,
            payment_no: logisticsResponse.data.paymentNo,
            cvs_validation_no: logisticsResponse.data.validationNo,
            tracking_number: logisticsResponse.data.trackingNumber
          });
        }
      }

      // Step 2: Get tracking number if needed
      if (!currentOrder.tracking_number && currentOrder.ecpay_logistics_id) {
        try {
          const trackingResponse = await api.get('/api/ecpay/logistics/tracking', {
            params: {
              logisticsId: currentOrder.ecpay_logistics_id,
              orderId: currentOrder.order_id
            }
          });

          if (trackingResponse.data?.success && trackingResponse.data.trackingNumber) {
            const trackingUpdate = {
              tracking_number: trackingResponse.data.trackingNumber,
              payment_no: trackingResponse.data.paymentNo,
              cvs_validation_no: trackingResponse.data.validationNo
            };

            await api.put(`/api/fukubukuro/admin/orders/${currentOrder.order_id}/status`, trackingUpdate);
            
            setOrders(prev => prev.map(o => 
              o.order_id === currentOrder.order_id ? { ...o, ...trackingUpdate } : o
            ));

            enqueueSnackbar(`已獲取追蹤號碼: ${trackingResponse.data.trackingNumber}`, { variant: 'success' });
          }
        } catch (err) {
          console.error('Error getting tracking:', err);
        }
      }

      // Step 3: Mark as shipped
      // For COD orders (store_cod), automatically mark as paid when shipped
      const updatePayload: any = {
        is_sent: true,
        status: 'shipped'
      };
      
      if (currentOrder.payment_method === 'store_cod') {
        updatePayload.is_received = true;
      }
      
      await api.put(`/api/fukubukuro/admin/orders/${currentOrder.order_id}/status`, updatePayload);

      // Update local state with both is_sent and is_received for COD
      const stateUpdate: any = { is_sent: true, status: 'shipped' };
      if (currentOrder.payment_method === 'store_cod') {
        stateUpdate.is_received = true;
      }
      
      setOrders(prev => prev.map(o =>
        o.order_id === currentOrder.order_id ? { ...o, ...stateUpdate } : o
      ));

      // Keep the row visible in the unshipped-only view until the next fetch
      setRecentlyShipped(prev => {
        const next = new Set(prev);
        next.add(currentOrder.order_id);
        return next;
      });

      enqueueSnackbar(
        currentOrder.payment_method === 'store_cod' 
          ? '訂單已標記為已出貨並已收款 (貨到付款)' 
          : '訂單已標記為已出貨', 
        { variant: 'success' }
      );

    } catch (error) {
      console.error('Error shipping order:', error);
      enqueueSnackbar('出貨處理失敗', { variant: 'error' });
    } finally {
      setProcessingShipping(false);
      setShippingDialogOpen(false);
    }
  };

  // Print single shipping document
  const handlePrintShippingDocument = async (order: FukubukuroOrder) => {
    if (!order?.order_id) return;
    
    try {
      enqueueSnackbar('正在準備出貨單...', { variant: 'info' });
      
      if (order.delivery_method === 'pickup' && order.store_id) {
        const response = await api.post(`/api/ecpay/logistics/print/${order.order_id}`, {
          platform: 'web',
          logistics_sub_type: order.logistics_sub_type
        });
        
        if (response?.data) {
          const blob = new Blob([response.data], { type: 'text/html' });
          const url = URL.createObjectURL(blob);
          window.open(url, '_blank');
          setTimeout(() => URL.revokeObjectURL(url), 100);
          enqueueSnackbar('出貨單已在新視窗中開啟', { variant: 'success' });
        }
      } else {
        enqueueSnackbar('宅配訂單需要手動處理', { variant: 'warning' });
      }
    } catch (error) {
      console.error('Error printing shipping document:', error);
      enqueueSnackbar('無法產生出貨單', { variant: 'error' });
    }
  };

  // Batch selection handlers
  const toggleOrderSelection = (orderId: string) => {
    setSelectedOrders(prev => 
      prev.includes(orderId) ? prev.filter(id => id !== orderId) : [...prev, orderId]
    );
  };

  const selectAllVisibleOrders = () => {
    const eligibleOrders = filteredOrders
      .filter(order => 
        order.delivery_method === 'pickup' && 
        order.store_id && 
        order.ecpay_logistics_id
      )
      .map(order => order.order_id);
    
    setSelectedOrders(eligibleOrders);
  };

  const clearAllSelections = () => {
    setSelectedOrders([]);
  };

  // Batch print
  const handleBatchPrint = () => {
    if (selectedOrders.length === 0) {
      enqueueSnackbar('請選擇要列印的訂單', { variant: 'warning' });
      return;
    }
    setBatchPrintDialogOpen(true);
  };

  const confirmBatchPrint = async () => {
    try {
      setBatchPrintLoading(true);
      
      // Group by logistics type
      const ordersByType: Record<string, FukubukuroOrder[]> = {};
      
      selectedOrders.forEach(orderId => {
        const order = orders.find(o => o.order_id === orderId);
        if (order && order.delivery_method === 'pickup' && order.store_id && order.ecpay_logistics_id) {
          const logisticsType = order.logistics_sub_type || 'UNIMARTC2C';
          if (!ordersByType[logisticsType]) {
            ordersByType[logisticsType] = [];
          }
          ordersByType[logisticsType].push(order);
        }
      });
      
      // Print each group
      for (const [logisticsType, typeOrders] of Object.entries(ordersByType)) {
        const logisticsIds: string[] = [];
        const paymentNos: string[] = [];
        const validationNos: string[] = [];
        
        typeOrders.forEach(order => {
          if (order.ecpay_logistics_id && order.payment_no) {
            logisticsIds.push(order.ecpay_logistics_id);
            paymentNos.push(order.payment_no);
            
            if (logisticsType === 'FAMIC2C') {
              validationNos.push('');
            } else {
              validationNos.push(order.cvs_validation_no || order.payment_no.slice(-4) || '');
            }
          }
        });
        
        const response = await api.post('/api/ecpay/logistics/batch-print', {
          logisticsIds: logisticsIds.join(','),
          paymentNos: paymentNos.join(','),
          validationNos: validationNos.join(','),
          documentType: logisticsType,
          orderIds: typeOrders.map(o => o.order_id)
        });
        
        if (response.data) {
          const blob = new Blob([response.data], { type: 'text/html' });
          const url = URL.createObjectURL(blob);
          window.open(url, '_blank');
          setTimeout(() => URL.revokeObjectURL(url), 100);
        }
        
        enqueueSnackbar(
          `${typeOrders.length} 張 ${logisticsType === 'FAMIC2C' ? '全家' : '7-ELEVEN'} 出貨單已開啟`, 
          { variant: 'success' }
        );
      }
      
      setSelectedOrders([]);
      
    } catch (error) {
      console.error('Batch print error:', error);
      enqueueSnackbar('批次列印失敗', { variant: 'error' });
    } finally {
      setBatchPrintLoading(false);
      setBatchPrintDialogOpen(false);
    }
  };

  // Get payment method info
  const getPaymentMethodInfo = (paymentMethod?: string) => {
    if (paymentMethod === 'credit_card') {
      return { label: '信用卡', icon: '💳', color: 'success' as const };
    } else if (paymentMethod === 'line_pay') {
      return { label: 'LINE Pay', icon: '💚', color: 'success' as const };
    } else if (paymentMethod === 'store_cod') {
      return { label: '貨到付款', icon: '💰', color: 'primary' as const };
    }
    return { label: '未指定', icon: '❓', color: 'default' as const };
  };

  // Get delivery method info
  const getDeliveryMethodInfo = (method?: string, logisticsSubType?: string) => {
    if (method === 'pickup') {
      if (logisticsSubType?.includes('FAMIC2C')) return '超商(全家)';
      if (logisticsSubType?.includes('UNIMARTC2C')) return '超商(7-11)';
      if (logisticsSubType?.includes('HILIFEC2C')) return '超商(萊爾富)';
      if (logisticsSubType?.includes('OKMARTC2C')) return '超商(OK mart)';
      return '超商取貨';
    } else if (method === 'shipping') {
      return '宅配';
    }
    return '未指定';
  };

  // Batch Actions Toolbar
  const BatchActionsToolbar = () => {
    const eligibleCount = filteredOrders.filter(order => 
      order.delivery_method === 'pickup' && order.store_id && order.ecpay_logistics_id
    ).length;
    
    return (
      <Box sx={{ 
        mb: 2, 
        p: 2, 
        bgcolor: 'background.paper', 
        border: 1, 
        borderColor: 'divider',
        borderRadius: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2">
            批次操作: 已選擇 {selectedOrders.length} / {eligibleCount} 個可列印訂單
          </Typography>
          <Button 
            size="small" 
            onClick={selectAllVisibleOrders}
            disabled={selectedOrders.length === eligibleCount}
          >
            全選
          </Button>
          <Button 
            size="small" 
            onClick={clearAllSelections}
            disabled={selectedOrders.length === 0}
          >
            清除選擇
          </Button>
        </Box>
        
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handleBatchPrint}
          disabled={selectedOrders.length === 0}
          color="primary"
        >
          批次列印 ({selectedOrders.length})
        </Button>
      </Box>
    );
  };

  // Batch Print Dialog
  const BatchPrintDialog = () => {
    const selectedOrderDetails = selectedOrders
      .map(orderId => orders.find(o => o.order_id === orderId))
      .filter((order): order is FukubukuroOrder => order !== null);
    
    const ordersByType = selectedOrderDetails.reduce((acc, order) => {
      const logisticsType = order.logistics_sub_type || 'UNIMARTC2C';
      if (!acc[logisticsType]) {
        acc[logisticsType] = [];
      }
      acc[logisticsType].push(order);
      return acc;
    }, {} as Record<string, FukubukuroOrder[]>);
    
    return (
      <Dialog
        open={batchPrintDialogOpen}
        onClose={() => setBatchPrintDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          批次列印確認
          <IconButton
            onClick={() => setBatchPrintDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            您即將批次列印 {selectedOrders.length} 張出貨單。不同超商類型將分別開啟新視窗。
          </Alert>
          
          {Object.entries(ordersByType).map(([logisticsType, typeOrders]) => (
            <Box key={logisticsType} sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <PrintIcon sx={{ mr: 1 }} />
                {logisticsType === 'FAMIC2C' ? '全家超商' : '7-ELEVEN'} 
                <Chip label={`${typeOrders.length} 張`} size="small" sx={{ ml: 1 }} />
              </Typography>
              
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>訂單編號</TableCell>
                      <TableCell>客戶</TableCell>
                      <TableCell>門市</TableCell>
                      <TableCell>物流編號</TableCell>
                      <TableCell>寄貨編號</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {typeOrders.map((order) => (
                      <TableRow key={order.order_id}>
                        <TableCell>#{order.order_id}</TableCell>
                        <TableCell>{order.customer_name || order.customer_phone}</TableCell>
                        <TableCell>
                          <Typography variant="body2">{order.store_name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {order.store_id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {order.ecpay_logistics_id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {order.payment_no}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ))}
          
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>注意事項：</strong><br />
              • 不同超商類型將分別開啟新的列印視窗<br />
              • 請確保您的瀏覽器允許彈出視窗<br />
              • 列印完成後，此對話框將自動關閉並清除選擇
            </Typography>
          </Alert>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setBatchPrintDialogOpen(false)} disabled={batchPrintLoading}>
            取消
          </Button>
          <Button 
            onClick={confirmBatchPrint}
            variant="contained"
            disabled={batchPrintLoading}
            startIcon={batchPrintLoading ? <CircularProgress size={20} /> : <PrintIcon />}
          >
            {batchPrintLoading ? '處理中...' : `確認列印 ${selectedOrders.length} 張`}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        福袋管理
      </Typography>

      {/* Main Tabs */}
      <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
        <Tab label={`商品管理 (${products.length})`} />
        <Tab label={`訂單管理 (${orders.length})`} />
      </Tabs>

      {/* ============ PRODUCTS TAB ============ */}
      {tabValue === 0 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Tabs value={productTabValue} onChange={(_, newValue) => setProductTabValue(newValue)}>
              <Tab label="全部商品" />
              <Tab label="已上架" />
              <Tab label="已停用" />
            </Tabs>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              新增福袋
            </Button>
          </Box>

          {loadingProducts ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2}>
              {filteredProducts.map((product) => (
                <Grid item xs={12} sm={6} md={4} key={product.id}>
                  <Card>
                    <Box sx={{ position: 'relative' }}>
                      <CardMedia
                        component="img"
                        height="200"
                        image={product.image_url}
                        alt={product.name}
                      />
                      <Chip
                        label={product.is_active ? '已上架' : '已停用'}
                        color={product.is_active ? 'success' : 'default'}
                        size="small"
                        sx={{ position: 'absolute', top: 8, right: 8 }}
                      />
                      {product.available_quantity <= 5 && (
                        <Chip
                          label={`庫存剩餘 ${product.available_quantity}`}
                          color="warning"
                          size="small"
                          sx={{ position: 'absolute', top: 8, left: 8 }}
                        />
                      )}
                    </Box>
                    <CardContent>
                      <Typography variant="h6" gutterBottom noWrap>
                        {product.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ 
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        mb: 1
                      }}>
                        {product.description}
                      </Typography>
                      {product.tags && product.tags.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                          {product.tags.slice(0, 3).map((tag, index) => (
                            <Chip key={index} label={tag} size="small" />
                          ))}
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                        <Typography variant="h6" color="primary">
                          NT$ {Math.floor(product.discount_price)}
                        </Typography>
                        {product.price > product.discount_price && (
                          <Typography variant="body2" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                            NT$ {Math.floor(product.price)}
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        庫存: {product.available_quantity} / {product.total_quantity}
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button size="small" startIcon={<EditIcon />} onClick={() => handleOpenDialog(product)}>
                        編輯
                      </Button>
                      <Button
                        size="small"
                        color={product.is_active ? 'warning' : 'success'}
                        onClick={() => handleToggleActive(product)}
                      >
                        {product.is_active ? '停用' : '啟用'}
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {/* ============ ORDERS TAB ============ */}
      {tabValue === 1 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">訂單列表</Typography>
            <Button 
              variant="outlined" 
              startIcon={<RefreshIcon />} 
              onClick={fetchOrders}
              disabled={loading}
            >
              重新整理
            </Button>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Switch
                checked={showUnshippedOnly}
                onChange={(e) => setShowUnshippedOnly(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocalShippingIcon fontSize="small" />
                <Typography variant="body2">
                  只顯示未發貨訂單
                </Typography>
                {showUnshippedOnly && (
                  <Chip 
                    label={`${filteredOrders.length} 筆`} 
                    size="small" 
                    color="warning"
                  />
                )}
              </Box>
            }
          />
          
          {showUnshippedOnly && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => setShowUnshippedOnly(false)}
              startIcon={<CloseIcon />}
            >
              清除篩選
            </Button>
          )}
        </Box>
          
          {/* Search */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="搜尋訂單編號、客戶姓名、電話..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <CloseIcon />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }}
            />
          </Box>

          {/* Batch Actions Toolbar */}
          <BatchActionsToolbar />
          
          {/* Orders List */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredOrders.length === 0 ? (
            <Alert severity="info">
              {searchTerm ? '沒有符合的訂單' : '沒有訂單資料'}
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {filteredOrders.map((order) => (
                <Paper 
                  key={order.order_id} 
                  elevation={2} 
                  sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}
                >
                  {/* Order Header */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {/* Checkbox for batch printing */}
                      {order.delivery_method === 'pickup' && 
                       order.store_id && 
                       order.ecpay_logistics_id && (
                        <Checkbox
                          checked={selectedOrders.includes(order.order_id)}
                          onChange={() => toggleOrderSelection(order.order_id)}
                          size="small"
                          color="primary"
                        />
                      )}
                      <Typography variant="h6">訂單 #{order.order_id}</Typography>
                      <Chip 
                        label="福袋" 
                        size="small" 
                        color="secondary"
                        variant="outlined"
                      />
                      {(() => {
                        const paymentInfo = getPaymentMethodInfo(order.payment_method);
                        return (
                          <Chip 
                            label={paymentInfo.label}
                            size="small"
                            color={paymentInfo.color}
                            variant="filled"
                            icon={<span style={{ fontSize: '14px' }}>{paymentInfo.icon}</span>}
                          />
                        );
                      })()}
                    </Box>
                    
                    {/* Order Actions */}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {!order.is_sent && order.delivery_method === 'pickup' && (
                        <Button
                          size="small"
                          color="primary"
                          variant="contained"
                          startIcon={<LocalShippingIcon />}
                          onClick={() => handleShipOrder(order)}
                        >
                          出貨
                        </Button>
                      )}
                      
                      {order.delivery_method === 'pickup' && 
                       order.store_id && 
                       order.ecpay_logistics_id && (
                        <IconButton 
                          size="small" 
                          color="secondary"
                          title="列印出貨單"
                          onClick={() => handlePrintShippingDocument(order)}
                        >
                          <PrintIcon />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                  
                  {/* Order Info Grid */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">客戶資訊</Typography>
                      <Typography variant="body1">{order.customer_name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {order.customer_phone}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="body2" color="text.secondary">配送方式</Typography>
                      <Typography variant="body1">
                        {getDeliveryMethodInfo(order.delivery_method, order.logistics_sub_type)}
                      </Typography>
                      {order.store_name && (
                        <Typography variant="body2" color="text.secondary">
                          {order.store_name}
                        </Typography>
                      )}
                    </Box>
                    
                    <Box>
                      <Typography variant="body2" color="text.secondary">訂單金額</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                        <Typography variant="h6">
                          ${Math.floor(parseFloat(order.discount_price?.toString() || '0'))}
                        </Typography>
                        {parseFloat(order.price?.toString() || '0') > parseFloat(order.discount_price?.toString() || '0') && (
                          <Typography variant="body2" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                            ${Math.floor(parseFloat(order.price?.toString() || '0'))}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    
                    <Box>
                      <Typography variant="body2" color="text.secondary">追蹤號碼</Typography>
                      {order.tracking_number || order.payment_no ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body1" sx={{ mr: 1 }}>
                            {order.tracking_number || (
                              order.logistics_sub_type === 'FAMIC2C'
                                ? order.payment_no 
                                : order.cvs_validation_no 
                                  ? `${order.payment_no}${order.cvs_validation_no}` 
                                  : order.payment_no
                            )}
                          </Typography>
                          <IconButton 
                            size="small" 
                            onClick={() => copyToClipboard(
                              order.tracking_number || order.payment_no
                            )}
                            title="複製追蹤碼"
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <Typography variant="body1" color="text.secondary">尚未出貨</Typography>
                      )}
                    </Box>
                  </Box>
                  
                  {/* Order Status Chips */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Chip 
                      label={order.is_received ? "已收到" : "未收到"} 
                      size="small" 
                      color={order.is_received ? "success" : "default"}
                      icon={order.is_received ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                      onClick={() => toggleReceivedStatus(order.order_id, order.is_received)}
                      clickable
                    />
                    <Chip 
                      label={order.is_sent ? "已發貨" : "未發貨"} 
                      size="small" 
                      color={order.is_sent ? "success" : "default"}
                      icon={order.is_sent ? <LocalShippingIcon /> : <InventoryIcon />}
                      onClick={() => toggleSentStatus(order.order_id, order.is_sent)}
                      clickable
                    />
                  </Box>
                  
                  {/* Product Info */}
                  {order.product_name && (
                    <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>商品資訊</Typography>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        {order.product_image && (
                          <Box
                            sx={{
                              width: 60,
                              height: 60,
                              cursor: 'pointer',
                              '&:hover': { opacity: 0.8 }
                            }}
                            onClick={() => handleOpenImageGallery(order)}
                          >
                            <img 
                              src={order.product_image} 
                              alt={order.product_name} 
                              style={{ 
                                width: '100%', 
                                height: '100%', 
                                objectFit: 'contain',
                                borderRadius: 4,
                                border: '1px solid #e0e0e0'
                              }} 
                            />
                          </Box>
                        )}
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1">{order.product_name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            身高: {order.height}cm | 體重: {order.weight}kg
                            {order.waist && ` | 腰圍: ${order.waist}cm`}
                          </Typography>
                          {order.notes && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              備註: {order.notes}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Paper>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* ============ PRODUCT ADD/EDIT DIALOG ============ */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {editingProduct ? '編輯福袋商品' : '新增福袋商品'}
            </Typography>
            <IconButton onClick={() => setDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={3}>
            {/* Basic Info */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                基本資訊
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="商品名稱"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                multiline
                rows={3}
                label="商品描述"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="分類"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                placeholder="例如: 男裝、女裝"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="標籤"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                placeholder="以逗號分隔，例如: 潮流,時尚,限量"
                helperText="以逗號分隔多個標籤"
              />
            </Grid>

            {/* Pricing */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                價格設定
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="number"
                label="原價"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                InputProps={{
                  startAdornment: <InputAdornment position="start">NT$</InputAdornment>,
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="number"
                label="最低價格（折扣價）"
                value={formData.discount_price}
                onChange={(e) => setFormData({ ...formData, discount_price: parseFloat(e.target.value) || 0 })}
                InputProps={{
                  startAdornment: <InputAdornment position="start">NT$</InputAdornment>,
                }}
                helperText="客戶支付的最低金額"
              />
            </Grid>

            {/* Stock */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                庫存管理
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="number"
                label="可售數量"
                value={formData.available_quantity}
                onChange={(e) => setFormData({ ...formData, available_quantity: parseInt(e.target.value) || 0 })}
                inputProps={{ min: 0 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                }
                label="立即上架"
              />
            </Grid>

            {/* Images */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                商品圖片
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="主要圖片網址"
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                placeholder="https://example.com/image.jpg"
              />
              {formData.image_url && (
                <Box sx={{ mt: 2 }}>
                  <img
                    src={formData.image_url}
                    alt="主圖預覽"
                    style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain' }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </Box>
              )}
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                額外圖片（選填）
              </Typography>
              {formData.all_images.map((img, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="圖片網址"
                    value={img}
                    onChange={(e) => {
                      const newImages = [...formData.all_images];
                      newImages[index] = e.target.value;
                      setFormData({ ...formData, all_images: newImages });
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => {
                      const newImages = formData.all_images.filter((_, i) => i !== index);
                      setFormData({ ...formData, all_images: newImages });
                    }}
                    disabled={formData.all_images.length === 1}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              ))}
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => {
                  setFormData({
                    ...formData,
                    all_images: [...formData.all_images, '']
                  });
                }}
              >
                新增圖片
              </Button>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            取消
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveProduct}
            disabled={!formData.name || !formData.description || formData.discount_price <= 0}
          >
            {editingProduct ? '更新' : '新增'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============ SHIPPING CONFIRMATION DIALOG ============ */}
      <Dialog
        open={shippingDialogOpen}
        onClose={() => setShippingDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>確認出貨</DialogTitle>
        <DialogContent>
          {currentOrder && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                您即將標記以下訂單為已出貨狀態。
              </Alert>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body1">
                  <strong>訂單編號:</strong> {currentOrder.order_id}
                </Typography>
                <Typography variant="body1">
                  <strong>商品:</strong> {currentOrder.product_name || '福袋'}
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="收件人姓名"
                    value={editableName}
                    onChange={(e) => setEditableName(e.target.value)}
                    sx={{ mb: 1 }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="收件人電話"
                    value={editablePhone}
                    onChange={(e) => setEditablePhone(e.target.value)}
                  />
                </Box>
                <Typography variant="body1">
                  <strong>配送方式:</strong> {getDeliveryMethodInfo(currentOrder.delivery_method, currentOrder.logistics_sub_type)}
                </Typography>
                {currentOrder.store_name && (
                  <Typography variant="body1">
                    <strong>取貨門市:</strong> {currentOrder.store_name} ({currentOrder.store_id})
                  </Typography>
                )}
                
                {(currentOrder.tracking_number || currentOrder.payment_no) && (
                  <Typography variant="body1" color="primary">
                    <strong>追蹤號碼:</strong> {currentOrder.tracking_number || currentOrder.payment_no}
                  </Typography>
                )}
                
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>金額:</strong>
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={editableAmount}
                    onChange={(e) => setEditableAmount(e.target.value)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    helperText={`原價格: $${Math.floor(parseFloat(currentOrder.discount_price?.toString() || currentOrder.price?.toString() || '0'))}`}
                  />
                </Box>
              </Box>
              
              <Alert severity="warning">
                確認後，系統將自動更新訂單狀態。請確保您已準備好出貨。
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShippingDialogOpen(false)} disabled={processingShipping}>
            取消
          </Button>
          <Button 
            onClick={confirmShipOrder} 
            color="primary" 
            variant="contained"
            disabled={processingShipping}
            startIcon={processingShipping ? <CircularProgress size={20} /> : <LocalShippingIcon />}
          >
            {processingShipping ? '處理中...' : '確認出貨'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* ============ BATCH PRINT DIALOG ============ */}
      <BatchPrintDialog />
      
      {/* ============ IMAGE GALLERY DIALOG ============ */}
      <Dialog
        open={imageGalleryOpen}
        onClose={() => setImageGalleryOpen(false)}
        maxWidth="lg"
        fullWidth
        onKeyDown={handleKeyPress}
        PaperProps={{
          sx: { backgroundColor: 'black', boxShadow: 24 }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: 'rgba(0, 0, 0, 0.9)', 
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6">{selectedProductTitle}</Typography>
          <IconButton onClick={() => setImageGalleryOpen(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ position: 'relative', p: 0, backgroundColor: 'black' }}>
          {currentProductImages.length > 1 && (
            <>
              <IconButton
                onClick={handlePreviousImage}
                sx={{
                  position: 'absolute',
                  left: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'white',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' },
                  zIndex: 1
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
              
              <IconButton
                onClick={handleNextImage}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'white',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' },
                  zIndex: 1
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </>
          )}
          
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh',
            maxHeight: '80vh'
          }}>
            {currentProductImages[currentImageIndex] && (
              <img
                src={currentProductImages[currentImageIndex]}
                alt={`${selectedProductTitle} - ${currentImageIndex + 1}`}
                style={{
                  maxWidth: '100%',
                  maxHeight: '80vh',
                  objectFit: 'contain'
                }}
              />
            )}
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ backgroundColor: 'rgba(0, 0, 0, 0.9)' }}>
          <Typography variant="caption" sx={{ color: 'white', flex: 1, ml: 2 }}>
            提示: 使用 ← → 方向鍵導覽，ESC 關閉
          </Typography>
          <Button onClick={() => setImageGalleryOpen(false)} sx={{ color: 'white' }}>
            關閉
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default FukubukuroFullAdminPage;